import { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, Trash2, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { config } from '../../config';
import { useAdminHeaders } from '../../auth/useAdminHeaders';

interface Maintenance {
  id: string;
  title: string;
  summary: string;
  state: string;
  scheduled_start: string;
  scheduled_end: string;
  created_at: string;
}

export default function MaintenancesAdmin() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    scheduled_start: '',
    scheduled_end: '',
    state: 'SCHEDULED',
    componentIds: [] as string[],
  });

  const apiUrl = config.apiUrl;
  const internalApiUrl = config.internalApiUrl;
  const getHeaders = useAdminHeaders();

  useEffect(() => {
    fetchMaintenances();
  }, []);

  async function fetchMaintenances() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/maintenances`);
      if (response.ok) {
        const data = await response.json();
        setMaintenances(data);
      }
    } catch (error) {
      console.error('Failed to fetch maintenances', error);
    }
  }

  async function handleCreate() {
    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/maintenances`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowCreateForm(false);
        setFormData({
          title: '',
          summary: '',
          scheduled_start: '',
          scheduled_end: '',
          state: 'SCHEDULED',
          componentIds: [],
        });
        fetchMaintenances();
      }
    } catch (error) {
      console.error('Failed to create maintenance', error);
    }
  }

  async function handleUpdate(id: string) {
    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/maintenances/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setEditingId(null);
        setFormData({
          title: '',
          summary: '',
          scheduled_start: '',
          scheduled_end: '',
          state: 'SCHEDULED',
          componentIds: [],
        });
        fetchMaintenances();
      }
    } catch (error) {
      console.error('Failed to update maintenance', error);
    }
  }

  function startEdit(maintenance: Maintenance) {
    setEditingId(maintenance.id);
    setFormData({
      title: maintenance.title,
      summary: maintenance.summary,
      scheduled_start: maintenance.scheduled_start,
      scheduled_end: maintenance.scheduled_end,
      state: maintenance.state,
      componentIds: [],
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setFormData({
      title: '',
      summary: '',
      scheduled_start: '',
      scheduled_end: '',
      state: 'SCHEDULED',
      componentIds: [],
    });
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this maintenance?')) return;

    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/maintenances/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (response.ok) {
        fetchMaintenances();
      }
    } catch (error) {
      console.error('Failed to delete maintenance', error);
    }
  }

  const inputClass = "w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all";

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-bold text-navy-900">Maintenance Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-navy-900 text-white font-semibold text-sm rounded-lg hover:bg-navy-800 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Schedule Maintenance</span>
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5 mb-5 animate-slide-down">
          <h3 className="text-sm font-bold text-navy-900 mb-4">Schedule New Maintenance</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Summary</label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                className={inputClass}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_start}
                  onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_end}
                  onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm bg-navy-900 text-white font-semibold rounded-lg hover:bg-navy-800 transition-colors"
            >
              Schedule Maintenance
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {maintenances.length === 0 ? (
          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-12 text-center">
            <p className="text-sm font-medium text-gray-500">No scheduled maintenances</p>
            <p className="text-xs text-gray-400 mt-1">Schedule one to get started</p>
          </div>
        ) : (
          maintenances.map((maintenance) => (
            <div key={maintenance.id} className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-1.5">
                    <h4 className="text-sm font-bold text-navy-900">{maintenance.title}</h4>
                    <span className="px-2 py-0.5 text-[11px] font-semibold rounded-md bg-blue-50 text-blue-700">
                      {maintenance.state}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{maintenance.summary}</p>
                  <div className="flex items-center gap-4 text-[11px] text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>Start: {format(new Date(maintenance.scheduled_start), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>End: {format(new Date(maintenance.scheduled_end), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <button
                    onClick={() => startEdit(maintenance)}
                    className="p-1.5 text-gray-400 hover:text-navy-900 hover:bg-gray-100 rounded-md transition-colors"
                    title="Edit maintenance"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(maintenance.id)}
                    className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-rose-50 rounded-md transition-colors"
                    title="Delete maintenance"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {editingId === maintenance.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 animate-slide-down">
                  <h4 className="text-xs font-bold text-navy-900 mb-3">Edit Maintenance</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Summary</label>
                      <textarea
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        className={inputClass}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Scheduled Start</label>
                        <input
                          type="datetime-local"
                          value={formData.scheduled_start}
                          onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Scheduled End</label>
                        <input
                          type="datetime-local"
                          value={formData.scheduled_end}
                          onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                      <select
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="SCHEDULED">Scheduled</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end space-x-2">
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdate(maintenance.id)}
                      className="px-3 py-1.5 text-xs bg-navy-900 text-white font-semibold rounded-md hover:bg-navy-800 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
